import type { SensorSummary, TelemetrySummary } from '@/apis/telemetry';
import {
  VITAL_ORDER,
  type VitalModalType,
  type VitalReadingSource,
} from '../../partials/sensorVitals';
import { formatReadingTime } from './time';

/** One sensor's shown value and its reading time per vital, plus the vitals that have a value to show. */
export interface SensorReadings {
  latest: Partial<Record<VitalModalType, number>>;
  lastAt: Partial<Record<VitalModalType, number>>;
  reported: VitalModalType[];
}

/** What one vital box shows when it renders from the telemetry summary. */
export interface SummaryVital {
  value: string | number | undefined;
  hasData: boolean;
  readingLines: string[];
}

/** The summary's sensor with this id, or its first sensor when no id is given. */
export const findSensor = (
  summary: TelemetrySummary,
  sensorId: string | null
): SensorSummary | null =>
  (sensorId
    ? summary.sensors.find((sensor) => sensor.id === sensorId)
    : summary.sensors[0]) ?? null;

/** Collects a sensor's vitals; once delivered, the shown value, and whether the vital has a value to show, come from its last reading before delivery. */
export const sensorReadings = (
  sensor: SensorSummary | null,
  delivered: boolean
): SensorReadings => {
  const readings: SensorReadings = {
    latest: {},
    lastAt: {},
    reported: [],
  };
  if (!sensor) return readings;
  for (const vital of VITAL_ORDER) {
    const measure = sensor.measures[vital];
    const reading = delivered
      ? measure?.at_delivery
      : measure && { at: measure.last_at, value: measure.latest };
    if (reading && reading.value !== null) {
      readings.reported.push(vital);
      readings.latest[vital] = reading.value;
      readings.lastAt[vital] = reading.at;
    }
  }
  return readings;
};

/** A vital box's value, whether it has data, and its tooltip lines; a map point the user selected overrides the value and counts as data. */
export const summaryVital = (
  field: VitalModalType,
  readings: SensorReadings,
  selectedPoint: VitalReadingSource | null,
  timeZone: string
): SummaryVital => {
  const picked = selectedPoint?.[field];
  const pointHasValue = picked != null && picked !== '';
  const value = pointHasValue ? picked : readings.latest[field];
  const hasData = pointHasValue || readings.reported.includes(field);
  if (selectedPoint) {
    return {
      value,
      hasData,
      readingLines: [`Selected point: ${selectedPoint.lat_updated}`],
    };
  }
  const readingLines: string[] = [];
  const at = readings.lastAt[field];
  if (at !== undefined) {
    readingLines.push(`Last reading: ${formatReadingTime(at, timeZone)}`);
  }
  return { value, hasData, readingLines };
};
